import { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { useInterval } from '@frontend/timer';
import { theme } from '@frontend/theme';
import { HOURS_LANE_WIDTH } from '../constants';
import { useCalendarView } from '../providers';
import {
  ADJUSTED_ONE_HOUR_IN_MINUTES,
  ADJUSTED_ONE_MINUTE_IN_MILLISECONDS,
  calculatePercentage,
  zIndex,
} from '../utils';

function calculateTimeProgress(totalDurationInHours: number, baseTimezone?: string) {
  const now = dayjs().tz(baseTimezone);
  const minutesPassedSinceOffset = now.hour() * 60 + now.minute();
  const progressPercentage = calculatePercentage(minutesPassedSinceOffset, totalDurationInHours * 60);

  return progressPercentage;
}

type ProgressLineProps = {
  percentage: number;
  hide?: boolean;
  color: string;
  scrollIntoView?: boolean;
};

const ProgressLine = ({ percentage, color, hide = false, scrollIntoView = false }: ProgressLineProps) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (hide || !scrollIntoView || !ref.current) return;

    // scroll vertically to the progress line (for current time progress line)
    ref.current.scrollIntoView({
      block: 'center',
      inline: 'nearest',
      behavior: 'smooth',
    });
  }, [hide, scrollIntoView]);

  if (hide) return null;

  return (
    <div ref={ref} css={[styles.container, { top: `${percentage}%` }]}>
      <div css={[styles.line, { background: color }]}>
        <div css={[styles.startingDot, { background: color, left: HOURS_LANE_WIDTH }]} />
      </div>
    </div>
  );
};

// Use to show the current time progress line
export const TimeProgress = ({ numberOfHours, baseTimezone }: { numberOfHours: number; baseTimezone?: string }) => {
  const [timeProgressPercentage, setTimeProgressPercentage] = useState(() =>
    calculateTimeProgress(numberOfHours, baseTimezone)
  );

  useInterval(() => {
    const progressPercentage = calculateTimeProgress(numberOfHours, baseTimezone);
    setTimeProgressPercentage(progressPercentage);
  }, ADJUSTED_ONE_MINUTE_IN_MILLISECONDS);

  return <ProgressLine percentage={timeProgressPercentage} color={theme.colors.primary50} scrollIntoView />;
};

// Use to show the hovered time progress line
export const HoveredTimeProgressLine = ({ numberOfHours }: { numberOfHours: number }) => {
  const { hoveredTimeProgressPosition, backgroundView } = useCalendarView([
    'hoveredTimeProgressPosition',
    'backgroundView',
  ]);
  const progressLinePosition = calculatePercentage(
    hoveredTimeProgressPosition,
    numberOfHours * ADJUSTED_ONE_HOUR_IN_MINUTES
  );

  return (
    <ProgressLine
      hide={backgroundView || hoveredTimeProgressPosition >= 24 * ADJUSTED_ONE_HOUR_IN_MINUTES}
      percentage={progressLinePosition}
      color={theme.colors.critical40}
    />
  );
};

const styles = {
  container: css({
    position: 'absolute',
    zIndex: zIndex.progressLine,
    height: '1.25px',
    width: '100%',
    left: 0,
    scrollMargin: '100px',
  }),
  line: css({
    position: 'absolute',
    left: 50,
    right: 0,
    top: '50%',
    height: '1px',
    transform: 'translateY(-50%)',
  }),
  startingDot: css({
    position: 'sticky',
    width: '11px',
    height: '11px',
    borderRadius: theme.borderRadius.full,
    top: '50%',
    transform: 'translateY(-50%)',
  }),
};
